import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as ArrowLeftIcon } from '../../../assets/icons/arrow-left.svg';
import { ReactComponent as CreditCardIcon } from '../../../assets/icons/credit-card.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  PaymentTransactionRecord,
  RequestPaymentTransactionsInterface,
} from '../../../interface/payment-transaction-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  disablePaymentTransactionFilterApplied,
  fetchPaymentTransactions,
  clearPaymentTransactionData,
  updatePaymentTransactionFilters,
  fetchPaymentTransactionFilters,
  setSortData,
  setGatewayFilterOptions,
} from '../../../redux/slices/PaymentTransactionSlice';
import CustomDateRangePicker, { calculateDateRange, normalizePresetKey } from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { getDaysBetween, removeEmptyKeys } from '../../../shared-functions/DateTime';
import HighlightText from '../../../shared-functions/HighlightText';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import paymentGatewayApi from '../../../utils/api/v2/payment-gateway';

/**
 * Get gateway name helper
 * @param {string} key - gateway key name
 * @returns {string} display name
 */
const getGatewayName = (key: string) => {
  const names: Record<string, string> = {
    stripe: 'Stripe',
    hyperpay: 'HyperPay',
    checkoutcom: 'Checkout.com',
    urway: 'Urway',
  };
  return names[key.toLowerCase()] || key;
};

/**
 * Payment Transactions list component
 * @returns {any} The rendered component
 */
function PaymentTransactions() {
  const { t, i18n } = useTranslation('paymentGateway');
  const navigate = useNavigate();
  const [searchParameters] = useSearchParams();
  const gatewayParameter = searchParameters.get('gateway');
  const dispatch = useAppDispatch();

  const {
    records,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.paymentTransactions);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const defaultRange = calculateDateRange(normalizePresetKey('last14Days'), 'future');
    return {
      startDate: defaultRange?.startDate || '',
      endDate: defaultRange?.endDate || '',
    }
  });

  /**
   * Handle the fetch data API request
   * @param {any} queryData - query filters and pagination params
   */
  const fetchData = (queryData: any) => {
    if (
      queryData.startDate &&
      queryData.endDate &&
      getDaysBetween(queryData.startDate, queryData.endDate) > 31
    ) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitError'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }
    if (!loading) {
      dispatch(fetchPaymentTransactions(removeEmptyKeys(queryData)));
    }
  };

  /**
   * Clears the date range values and triggers fetch with empty date range.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {};
    fetchData({
      page: DefaultApiLimit.page,
      limit,
      ...queryParameters,
      ...sortValue,
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };

  /**
   * Handle manual refresh
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  /**
   * Handle pagination limit/page change
   * @param {RequestPaymentTransactionsInterface} value - pagination values
   */
  const pageNateLimitChange = (value: RequestPaymentTransactionsInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      ...value,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    });
  };

  /**
   * Handle sort change
   * sort field and direction
   * @param {object} sortData - The sort data object containing sortField and sortBy
   * @param {string} sortData.sortField - The field to sort by
   * @param {'asc' | 'desc'} sortData.sortBy - The sort direction
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    /**
     * Load configured gateways dynamically to populate dropdown filters
     */
    const loadConfiguredGateways = async () => {
      try {
        const response = await paymentGatewayApi.fetchPaymentGatewayConfigList();
        const configList = response.data || [];
        const options = configList.map((g: any) => ({
          label: getGatewayName(g.gatewayKey),
          value: g.gatewayKey,
        }));
        dispatch(setGatewayFilterOptions(options));
      } catch (error) {
        console.error('Failed to load configured gateways', error);
      }
    };

    loadConfiguredGateways();
  }, [dispatch]);

  useEffect(() => {
    if (gatewayParameter) {
      dispatch(
        updatePaymentTransactionFilters({
          type: 'add',
          key: 'gateway',
          value: [gatewayParameter],
        }),
      );
    }
  }, [gatewayParameter, dispatch]);

  useEffect(() => {
    const hasUrlParameters = Boolean(gatewayParameter);
    const isReady = hasUrlParameters ? isFilterUpdated : true;

    if (
      isReady &&
      (isFilterUpdated ||
        records.length === 0 ||
        Boolean(dateRange.startDate) || Boolean(dateRange.endDate))
    ) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
      });
      dispatch(disablePaymentTransactionFilterApplied());
    }
  }, [filters, gatewayParameter, isFilterUpdated, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('paymentTransactions'),
        icon: <CreditCardIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults, dispatch, t]);

  useEffect(() => {
    return () => {
      dispatch(clearPaymentTransactionData());
    };
  }, [dispatch]);

  const searchFilter = filters?.find((filter) => filter.searchBy);
  const searchText = searchFilter?.value?.toString() || null;

  const columns = [
    {
      label: t('preAuthTransactionId'),
      accessorKey: 'preAuthTransactionId' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render pre-auth transaction ID
       * @param {any} row - row data
       * @returns {React.ReactNode} The rendered node
       */
      customData: (row: any) => (
        <span>{HighlightText(searchText, row.preAuthTransactionId || t('notAvailable'))}</span>
      ),
    },
    {
      label: t('transactionId'),
      accessorKey: 'transactionId' as keyof PaymentTransactionRecord,
      sortAllow: false,
    },
    {
      label: t('driverId'),
      accessorKey: 'userCustomId' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render custom user ID
       * @param {any} row - row data
       * @returns {React.ReactNode} The rendered node
       */
      customData: (row: any) => (
        <span>{HighlightText(searchText, row.userCustomId || t('notAvailable'))}</span>
      ),
    },
    {
      label: t('type'),
      accessorKey: 'type' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render type badge
       * @param {any} row - row data
       * @returns {React.ReactNode} The rendered node
       */
      customData: (row: any) => (
        <span className={`transaction-type-badge ${row.type?.toLowerCase() || ''}`}>
          {HighlightText(searchText, row.type || '')}
        </span>
      ),
    },
    {
      label: t('gateway'),
      accessorKey: 'gateway' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render gateway badge
       * @param {any} row - row data
       * @returns {React.ReactNode} The rendered node
       */
      customData: (row: any) => (
        <span className="gateway-text-badge">
          {HighlightText(searchText, getGatewayName(row.gateway || ''))}
        </span>
      ),
    },
    {
      label: t('amount'),
      accessorKey: 'amount' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render amount and currency
       * @param {any} row - row data
       * @returns {React.ReactNode} The rendered node
       */
      customData: (row: any) => (
        <span className="amount-text">
          {HighlightText(searchText, `${ToFixedValue(row.amount)} ${row.currency || ''}`.trim())}
        </span>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render status badge
       * @param {any} row - row data
       * @returns {React.ReactNode} The rendered node
       */
      customData: (row: any) => (
        <span className={`status-badge ${row.status?.toLowerCase() || ''}`}>
          {HighlightText(searchText, row.status || '')}
        </span>
      ),
    },
    {
      label: t('gatewayTransactionId'),
      accessorKey: 'gatewayTransactionId' as keyof PaymentTransactionRecord,
      sortAllow: false,
      /**
       * Render gateway transaction ID
       * @param {any} row - row data
       * @returns {React.ReactNode} The rendered node
       */
      customData: (row: any) => (
        <span>{HighlightText(searchText, row.gatewayTransactionId || t('notAvailable'))}</span>
      ),
    },
    {
      label: t('walletCreditedAmount'),
      accessorKey: 'walletCreditAmount' as keyof PaymentTransactionRecord,
      /**
       * Render credited amount
       * @param {PaymentTransactionRecord} row - row  data
       * @returns {React.ReactNode} the rendered node
       */
      customData: (row: PaymentTransactionRecord): React.ReactElement => (
        <span>{row?.walletCreditAmount ? `${row.walletCreditAmount} ${row.currency}` : '-'}</span>
      ),
    },
  ];

  return (
    <div className={`payment-transactions-container ${i18n.language === 'ar' ? 'rtl' : ''}`}>
      <div className="header-section">
        <div className="header-content">
          <ArrowLeftIcon
            className="back-arrow"
            onClick={() => navigate('/ev-admin/configuration/PaymentGateway')}
          />
          <div className="header-title-wrap">
            <h2>{t('paymentTransactions')}</h2>
            <div className="divider" />
            <p>{t('paymentTransactionsDescription')}</p>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <CustomTable
          dataKey="PaymentTransactions"
          rightTopContent={renderDateFilters()}
          onClearFilterChange={() => {
            clearDateRange();
          }}
          header={columns}
          data={records}
          version="v2"
          serialNo
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updatePaymentTransactionFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchPaymentTransactionFilters}
          refreshRequired
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          height={window.innerHeight >= 700 ? 337 : 342}
          created
          lastUpdated
        />
      </div>
    </div>
  );
}

export default PaymentTransactions;
